import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  Button,
  IconButton,
  InlineLoading,
  RadioTile,
  TileGroup,
} from '@carbon/react';
import { Close, TrashCan } from '@carbon/icons-react';
import {
  tcSelectedDevAreaAtom,
  tcOverlappingUnitsAtom,
  tcUnitDetailsByIdAtom,
  tcUnitResolutionPendingAtom,
} from 'pages/type-curves/type-curve-filters/atoms';
import {
  useCreateUnitOverrideMutation,
  useBulkRemoveUnitsMutation,
} from 'pages/type-curves/use-unit-overrides';
import { format0 } from 'utils/number';
import { activeTcWorkspaceAtom, tcDetailsRightPanelAtom } from '../atoms';
import { toastAtom } from 'pages/type-curves/atoms';
import styles from './unit-resolution-panel.module.scss';

interface ConflictUnit {
  id: number;
  name: string;
  overlapPct: number | null;
}

const formatAcres = (n: number): string => `${format0(n)} ac`;

// Right-panel drawer (atom-gated on tcDetailsRightPanelAtom === 'unit-resolution').
// Lists the clicked unit + its overlap partners; the user keeps one and removes
// the rest (footer CTA, sequential POSTs), or removes individual partners via the
// per-tile button. Each removal runs the refresh fan-out.
export const UnitResolutionPanel = () => {
  const [panel, setPanel] = useAtom(tcDetailsRightPanelAtom);
  const selected = useAtomValue(tcSelectedDevAreaAtom);
  const setSelected = useSetAtom(tcSelectedDevAreaAtom);
  const overlapping = useAtomValue(tcOverlappingUnitsAtom);
  const unitDetails = useAtomValue(tcUnitDetailsByIdAtom);
  const pending = useAtomValue(tcUnitResolutionPendingAtom);
  const workspace = useAtomValue(activeTcWorkspaceAtom);
  const createOverride = useCreateUnitOverrideMutation(workspace);
  const bulkRemove = useBulkRemoveUnitsMutation(workspace);
  const setToast = useSetAtom(toastAtom);

  const open = panel.type === 'unit-resolution' && panel.open;
  const selectedId = selected?.developmentAreaId ?? null;
  const selectedName = selected?.developmentAreaName ?? null;
  const [keepId, setKeepId] = useState<number | null>(selectedId);

  const group = useMemo<ConflictUnit[]>(() => {
    if (selectedId === null) return [];
    const entry = overlapping.find((o) => o.id === selectedId);
    const items: ConflictUnit[] = [
      {
        id: selectedId,
        name: selectedName ?? `U-${selectedId}`,
        overlapPct: null,
      },
      ...(entry?.overlappingUnits ?? []).map((p) => ({
        id: p.id,
        name: p.name,
        overlapPct: p.overlapPercentage,
      })),
    ];
    return items.filter(
      (it, i) => items.findIndex((x) => x.id === it.id) === i
    );
  }, [selectedId, selectedName, overlapping]);

  useEffect(() => {
    if (open) setKeepId(selectedId);
  }, [open, selectedId]);

  const close = useCallback(
    () => setPanel({ type: 'dca', open: false }),
    [setPanel]
  );

  // When a remove / "Keep" is in flight, defer closing the panel until the
  // dev-areas re-fetch settles (pending → false) so the panel doesn't vanish
  // ~1–2s before the map + list actually reflect the removals.
  const closeWhenSettledRef = useRef(false);

  const handleRemoveSingle = useCallback(
    async (id: number) => {
      const result = await createOverride({ unit_id: id, action: 'remove' });
      if (!result.ok) {
        setToast({
          kind: 'error',
          title: 'Failed to remove unit',
          subtitle: result.message,
        });
        return;
      }
      if (id === selectedId) {
        setSelected(null);
        close();
        return;
      }
      // Removing the last remaining partner resolves the overlap — close once
      // the refresh settles (same path as "Keep").
      if (group.length <= 2) {
        closeWhenSettledRef.current = true;
      }
    },
    [createOverride, selectedId, setSelected, close, group.length, setToast]
  );

  const handleKeep = useCallback(async () => {
    if (keepId === null) return;
    const partners = group.filter((g) => g.id !== keepId);
    if (partners.length === 0) {
      close();
      return;
    }
    // Keep the panel open (showing the loader); the effect below closes it once
    // the update lands. One bulk call → N remove POSTs but a single dev-areas
    // re-fetch, instead of one re-fetch per removed unit.
    closeWhenSettledRef.current = true;
    const result = await bulkRemove(partners.map((p) => p.id));
    if (!result.ok) {
      closeWhenSettledRef.current = false;
      setToast({
        kind: 'error',
        title: 'Failed to remove units',
        subtitle: result.message,
      });
    }
  }, [keepId, group, bulkRemove, close, setToast]);

  // Close once the "Keep" has fully applied — i.e. the dev-areas re-fetch
  // completed and cleared the pending loader.
  useEffect(() => {
    if (closeWhenSettledRef.current && !pending) {
      closeWhenSettledRef.current = false;
      close();
    }
  }, [pending, close]);

  // If the unit being resolved gets deselected — e.g. removed via the toolbar
  // bin, which clears the selection — close the drawer instead of leaving it
  // open and blank. (On open the selection is always set first, so this can't
  // fire spuriously.)
  useEffect(() => {
    if (open && selectedId === null) close();
  }, [open, selectedId, close]);

  if (!open || selectedId === null) return null;

  const keepName = group.find((g) => g.id === keepId)?.name ?? null;
  const removeCount = group.filter((g) => g.id !== keepId).length;

  return (
    <div
      className={styles.drawer}
      role="dialog"
      aria-label="Resolve overlapping units"
    >
      <header className={styles.header}>
        <div className={styles.headerText}>
          <h2 className={styles.title}>Resolve overlap</h2>
          <p className={styles.subtitle}>
            Pick the unit you want to keep — the{' '}
            {removeCount === 1 ? 'other one' : `other ${removeCount}`} will be
            removed from this Type Curve Area.
          </p>
        </div>
        <IconButton kind="ghost" label="Close" align="left" onClick={close}>
          <Close />
        </IconButton>
      </header>

      <div className={styles.body}>
        <TileGroup
          name="overlap-resolution-keep"
          valueSelected={keepId !== null ? String(keepId) : undefined}
          onChange={(value: string | number) => setKeepId(Number(value))}
          legend="Which unit do you want to keep?"
          className={styles.tileGroup}
        >
          {group.map((u) => {
            const detail = unitDetails[u.id];
            const sub =
              detail?.unitName && detail.unitName !== u.name
                ? detail.unitName
                : detail?.unitType;
            const acreage = detail?.acreage;
            const overlapAc =
              acreage !== undefined && u.overlapPct !== null
                ? (acreage * u.overlapPct) / 100
                : null;
            return (
              <RadioTile
                key={u.id}
                id={`overlap-keep-${u.id}`}
                value={String(u.id)}
                className={styles.unitTile}
              >
                <div
                  className={`${styles.unitCard} ${
                    u.id === keepId ? styles.unitCardSelected : ''
                  }`}
                >
                  <span className={styles.unitName}>{u.name}</span>
                  {sub && <span className={styles.unitSub}>{sub}</span>}
                  {(acreage !== undefined || u.overlapPct !== null) && (
                    <dl className={styles.unitMeta}>
                      {acreage !== undefined && (
                        <div className={styles.unitMetaRow}>
                          <dt>Unit area</dt>
                          <dd>{formatAcres(acreage)}</dd>
                        </div>
                      )}
                      {u.overlapPct !== null && (
                        <div className={styles.unitMetaRow}>
                          <dt>Overlap with others</dt>
                          <dd>
                            {overlapAc !== null && `${formatAcres(overlapAc)} `}
                            <span className={styles.unitMetaPct}>
                              ({u.overlapPct}% of this unit)
                            </span>
                          </dd>
                        </div>
                      )}
                    </dl>
                  )}
                  <div className={styles.tileActions}>
                    <Button
                      kind="ghost"
                      size="sm"
                      renderIcon={TrashCan}
                      disabled={pending}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleRemoveSingle(u.id);
                      }}
                    >
                      Remove from type curve
                    </Button>
                  </div>
                </div>
              </RadioTile>
            );
          })}
        </TileGroup>
      </div>

      <footer className={styles.footer}>
        {pending ? (
          <InlineLoading
            className={styles.footerLoading}
            description="Resolving overlap…"
          />
        ) : (
          <>
            <Button kind="ghost" size="md" onClick={close}>
              Cancel
            </Button>
            <Button
              kind="primary"
              size="md"
              disabled={keepId === null || group.length < 2}
              onClick={() => void handleKeep()}
            >
              {keepName ? `Keep ${keepName}` : 'Pick a unit to keep'}
            </Button>
          </>
        )}
      </footer>
    </div>
  );
};

UnitResolutionPanel.displayName = 'UnitResolutionPanel';
